<script setup lang="ts">
import {
  computed,
  h,
  nextTick,
  onBeforeUnmount,
  ref,
  useTemplateRef,
  watch,
  type Component,
  type PropType,
  type VNode,
} from 'vue';
import {
  IconAdd,
  IconArrowUp,
  IconAttach,
  IconDocumentLock,
  IconDocumentText,
  IconExpand,
  IconMore,
  IconStop,
  IconTime,
} from '@/components/design/icons';
import { NIcon, type DropdownOption, type UploadFileInfo } from 'naive-ui';
import {
  deleteThread,
  deleteThread3 as deleteProjectThread,
  getThreadById,
  getThreadById3 as getProjectThreadById,
  searchProjectThreads,
  searchThreads,
  updateThread,
  updateThread3 as updateProjectThread,
  TaskStatus,
  type ChatThreadInDb,
  type ChatThreadWithMessages,
  type GeneralChatStreamRequest,
  type LimitableSearchableQuery,
} from '@/api';
import {
  ChatStreamCancelledError,
  ChatStreamServerError,
  useChatStream,
} from '@/composables/useChatStream';
import { useChatStatusGroups } from '@/composables/useChatStatusGroups';
import { useConfirmationMode } from '@/composables/useConfirmationMode';
import { useVaultUploadDialog } from '@/composables/useVaultUploadDialog';
import { isRetiredChatModel, useModelSelection } from '@/composables/useModelSelection';
import { useUserAttentionStore } from '@/stores/userAttentionStore';
import { getWsClient, type ChatKind } from '@/services/WebSocketService';
import { MAX_ASK_AI_ITEMS } from '@/utils/askAi';
import {
  contextItemRoute,
  contextKey,
  fallbackContextKind,
  fallbackContextName,
  fetchContextName,
  parseReferencedItems,
  type ChatContextType,
  type ReferencedItem,
} from '@/utils/chat/contextItems';
import { isGenericThinkingLabel } from '@/utils/chat/thinkingMessages';
import { isArchive } from '@/utils/fileTypeUtils';
import { buildTitle, mergeContent, summarizeHitlSubmission } from '@/utils/chat/thread';
import { parseSelectedDocumentsMessage } from '@/utils/chat/selectedDocuments';
import { extractPendingInterrupt } from '@/utils/chat/pendingInterrupt';
import type { GaiaChatMessageItem } from '@/utils/chat/types';
import type { HitlSubmitPayload } from '@/components/humanInTheLoop/HitlInterruptCard.vue';
import {
  normalizeInterruptPayload,
  type NormalizedInterrupt,
} from '@/utils/humanInTheLoop/normalizeInterrupt';
import { logger } from '@/utils/logger';
import GaiaButton from '../../base/GaiaButton.vue';
import GaiaCreateButton from '../../base/GaiaCreateButton.vue';
import GaiaChip from '../../base/GaiaChip.vue';
import GaiaDropdown from '../../base/GaiaDropdown.vue';
import GaiaFileUpload from '../../base/GaiaFileUpload.vue';
import GaiaIconButton from '../../base/GaiaIconButton.vue';
import GaiaBanner from '../../composite/GaiaBanner.vue';
import GaiaListItem from '../../composite/GaiaListItem.vue';
import GaiaMenuSurface from '../../base/GaiaMenuSurface.vue';
import GaiaPopover from '../../base/GaiaPopover.vue';
import GaiaTag from '../../base/GaiaTag.vue';
import GaiaText from '../../base/GaiaText.vue';
import GaiaChatHistory from './GaiaChatHistory.vue';
import GaiaChatSettings from './GaiaChatSettings.vue';
import GaiaChatThread from './GaiaChatThread.vue';
import { gaiaMotion, gaiaNeutral, gaiaShadow, gaiaShellPadding } from '../../themes/tokens';
import GaiaInlineEdit from '../../base/inputs/GaiaInlineEdit.vue';
import GaiaPromptTextarea from '../../base/inputs/GaiaPromptTextarea.vue';
import { useGaiaToast } from '@/composables/useGaiaToast';

const props = defineProps({
  value: {
    type: String,
    default: '',
  },
  /**
   * Suggested questions offered under the composer, as secondary (pill) chips
   * — the variant the design system calls "composer suggestions". Empty means
   * no row is rendered. Hidden once a chat is open.
   */
  suggestions: {
    type: Array as PropType<string[]>,
    default: () => [],
  },
  placeholder: {
    type: String,
    default: 'Ask about your contracts — or drop a file anywhere on this page.',
  },
  /** Placeholder once a thread is open — the design's "Ask a follow-up". */
  followUpPlaceholder: {
    type: String,
    default: 'Ask a follow-up',
  },
  /**
   * Chat mode: the card has a transcript above it. Two-way, so the host can
   * collapse its own chrome (greeting, quick actions, right rail) in step, and
   * send the user back home from a nav item.
   */
  chatting: {
    type: Boolean,
    default: false,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  /**
   * Files can be attached to this composer, so the card is a drop target. Off
   * by default — a composer with nowhere to put files should not offer to take
   * them.
   */
  attachable: {
    type: Boolean,
    default: false,
  },
  /** Copy on the drag-time overlay. */
  dropHint: {
    type: String,
    default: 'Drop files to attach them',
  },
  /** Initials on the user's own message bubbles. */
  userInitials: {
    type: String,
    default: '',
  },
  /**
   * Narrow host: drop the chrome's labels to their icons and the settings pill
   * to its mode, so the thread header and the composer footer still fit on one
   * line. The review workspace's chat rail is 360px wide until it is widened,
   * which is a third of what these two rows were drawn for.
   */
  dense: {
    type: Boolean,
    default: false,
  },
  /**
   * Which chat this is. The kind picks the backend agent and the thread store,
   * so one component serves both the general chat and a review project's — the
   * host initialises it and everything else here follows from it.
   */
  chatKind: {
    type: String as PropType<Extract<ChatKind, 'general' | 'review_project'>>,
    default: 'general',
  },
  /**
   * `review_project` only: the project this chat is scoped to. Sent when a new
   * thread is started (an existing thread already carries its project), and
   * used to scope the history list to that project's conversations.
   */
  reviewProjectUid: {
    type: String,
    default: undefined,
  },
  /**
   * Keeps composer attachments inside this folder: the upload dialog starts
   * there and can't climb above it. Undefined starts at the vault root, which
   * is right for the general chat; a review project passes its data-room
   * directory so a file dropped on the chat joins the room under review.
   */
  uploadDirectoryUid: {
    type: String,
    default: undefined,
  },
  /** That folder's name, the upload dialog's first crumb. */
  uploadDirectoryLabel: {
    type: String,
    default: 'Data room',
  },
  /**
   * `review_project` only: the data-room file revisions currently selected, and
   * therefore the context this chat's next message carries. The selection lives
   * with the data room, not here — the host owns it, names them (this component
   * has no business reading the data room's store) and syncs removals back
   * through `remove-selected-revisions`.
   */
  selectedRevisions: {
    type: Array as PropType<{ uid: string; name?: string }[]>,
    default: () => [],
  },
});

const emit = defineEmits<{
  'update:value': [value: string];
  'update:chatting': [chatting: boolean];
  /**
   * The conversation on screen changed — a thread was opened, or a new one got
   * its uid from the backend. `null` means an unsaved new chat. Fired only when
   * the *conversation* changes, never on a refetch of the same one, so a host
   * can map it straight onto history entries.
   */
  'thread-change': [threadUid: string | null];
  /**
   * A referenced document was clicked. The path is ready to push — this
   * component never touches the router, since it is also mounted where there
   * isn't one (the design workbench).
   */
  'open-reference': [path: string];
  /** The chat sent its context, so the host may clear what it had selected. */
  'clear-selected-revisions': [];
  /** Uploads that became data-room files, for the host to fold into its selection. */
  'add-selected-revisions': [revisionUids: string[]];
  'remove-selected-revisions': [revisionUids: string[]];
  /**
   * A human-in-the-loop interrupt was answered. The review workspace acts on
   * some of these itself — confirming a report generation soft-locks its report
   * editor — which it can only do if it hears the answer.
   */
  'interrupt-decision': [decision: { interruptType: string | null; action: string }];
}>();

const isReviewProject = computed(() => props.chatKind === 'review_project');

/**
 * Revision uid per upload card, for the review-project kind: an uploaded file's
 * card stands for a data-room revision, so dropping the card has to deselect
 * the revision too — otherwise the next message still focuses on a file the
 * user just took off the composer.
 */
const uploadedRevisionUidByKey = ref<Record<string, string>>({});

/** What the next message carries — the payload wants uids, nothing more. */
const selectedRevisionUids = computed(() => props.selectedRevisions.map(item => item.uid));

/**
 * The selection as composer chips. An upload made from this composer is already
 * shown as its own card *and* joins the host's selection, so it is filtered out
 * here — otherwise attaching a file lists it twice, once per representation.
 */
const selectedRevisionChips = computed(() => {
  if (!isReviewProject.value) return [];
  const fromUploads = new Set(Object.values(uploadedRevisionUidByKey.value));
  return props.selectedRevisions.filter(item => !fromUploads.has(item.uid));
});

/**
 * Thread CRUD targets the store the kind belongs to: a review project's
 * conversations are listed per project and live behind their own endpoints, so
 * the general chat's history must not show them and vice versa.
 */
const threadApi = computed(() =>
  isReviewProject.value
    ? {
        // Typed as the general search's result, which this satisfies — both
        // endpoints answer with the same `ChatThreadSearchResult`.
        search: (options: { body: LimitableSearchableQuery }): ReturnType<typeof searchThreads> =>
          searchProjectThreads({
            ...options,
            query: { review_project_uid: props.reviewProjectUid ?? '' },
          }),
        getById: getProjectThreadById,
        update: updateProjectThread,
        remove: deleteProjectThread,
      }
    : { search: searchThreads, getById: getThreadById, update: updateThread, remove: deleteThread }
);

const messageApi = useGaiaToast();
const { openUploadDialog } = useVaultUploadDialog();
const userAttentionStore = useUserAttentionStore();
const { currentConfirmationMode } = useConfirmationMode();
const { currentChatModelSelection } = useModelSelection();

// ── Transcript state ────────────────────────────────────────────────────────

const messages = ref<GaiaChatMessageItem[]>([]);
const threadUid = ref<string | null>(null);
const threadTitle = ref('New conversation');
const isStreaming = ref(false);
const errorMessage = ref<string | null>(null);

const statusGroups = useChatStatusGroups();

/**
 * Which bubble the current run writes into. A plain `let` rather than a ref:
 * it is read inside the stream callbacks and never rendered.
 */
let currentAssistantIndex: number | null = null;
let isUnmounted = false;

function createId(): string {
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function writeToCurrentAssistant(mutate: (message: GaiaChatMessageItem) => void): void {
  if (currentAssistantIndex === null) return;
  // `.at()` rather than an index: the bubble can be gone (a reset mid-run).
  const message = messages.value.at(currentAssistantIndex);
  if (message) mutate(message);
}

const chatStream = useChatStream({
  kind: props.chatKind,
  onStatus: update => {
    statusGroups.handleStatus(update);
  },
  onToken: text => {
    writeToCurrentAssistant(message => {
      message.content = mergeContent(message.content, text);
    });
  },
  onThreadInfo: info => {
    // The backend names a brand-new thread mid-run: same conversation, now
    // addressable. Announce it once, not on every frame of the run.
    const isNewThread = threadUid.value !== info.thread_uid;
    threadUid.value = info.thread_uid;
    if (info.title.trim()) threadTitle.value = info.title.trim();
    if (isNewThread) {
      // `runStream` could not register this thread as the one on screen — it
      // had no uid until now. Without this the attention flag the run raises
      // when it settles reads as a background badge instead of being consumed.
      userAttentionStore.setActiveThread(info.thread_uid);
      emit('thread-change', info.thread_uid);
    }
  },
  onInterrupt: interrupt => {
    statusGroups.clear();
    const interrupts = normalizeInterruptPayload(interrupt);
    if (!interrupts.length) return;
    writeToCurrentAssistant(message => {
      message.interrupt = { interrupts };
    });
  },
  onError: (_level, message) => {
    errorMessage.value = message;
  },
});

/**
 * What the streaming bubble shows about the run: every open group, padded up to
 * five with the most recently finished ones. The padding is the point — tool
 * groups routinely open and close between paints, so showing only open groups
 * means the user never sees a tool call at all, just "Thinking…".
 *
 * Once answer tokens arrive the finished ones drop away and only still-running
 * work remains (a parallel tool may outlive the first token).
 */
const MAX_VISIBLE_STATUS_GROUPS = 5;
const visibleStatusGroups = computed(() => {
  const open = statusGroups.openGroups.value;
  if ((messages.value.at(-1)?.content ?? '').length > 0) return open;

  // A *finished* generic group ("Thinking…", which the backend opens around
  // every model turn) reports nothing worth keeping on screen — it would just
  // crowd out the real tool steps beside it. Finished tool groups stay.
  const doneSlots = Math.max(0, MAX_VISIBLE_STATUS_GROUPS - open.length);
  const done = statusGroups.groups.value.filter(
    group => group.done && !isGenericThinkingLabel(group.label)
  );
  const visibleDoneIds = new Set(done.slice(-doneSlots).map(group => group.id));
  return statusGroups.groups.value.filter(group => !group.done || visibleDoneIds.has(group.id));
});

// ── Scrolling ───────────────────────────────────────────────────────────────

const threadScroll = useTemplateRef<{ scrollTo: (options: ScrollToOptions) => void }>(
  'threadScroll'
);

function scrollToBottom(): void {
  void nextTick(() => {
    threadScroll.value?.scrollTo({ top: Number.MAX_SAFE_INTEGER, behavior: 'smooth' });
  });
}

// A growing answer should keep its tail in view.
watch(() => messages.value.at(-1)?.content, scrollToBottom);

// ── Composer context ────────────────────────────────────────────────────────

/**
 * What this question is pointed at. Two ways in, one shape out:
 * a dropped file is uploaded to the vault and becomes a `file` reference, and
 * "Ask AI" elsewhere in the app hands over uids that are already stored. Either
 * way the uid rides into the start frame and the chip is consumed by the send.
 */
interface ContextItem {
  /** Local key — a dropped file has no uid until its upload finishes. */
  key: string;
  type: ChatContextType;
  name: string;
  uid?: string;
  status: 'uploading' | 'ready' | 'failed';
  /** 0–100, so a large upload shows something other than a spinner. */
  progress: number;
}

const contextItems = ref<ContextItem[]>([]);

/** The three uid lists the general-chat start frame takes, by type. */
const readySelection = computed(() => {
  const selection: Record<ChatContextType, string[]> = {
    file: [],
    contract: [],
    contractFile: [],
  };
  for (const item of contextItems.value) {
    if (item.status === 'ready' && item.uid) selection[item.type].push(item.uid);
  }
  return selection;
});

/**
 * Sending now would drop these on the floor — their uids don't exist yet, and
 * the send consumes the chips either way. So the composer waits for them.
 */
const hasUploadsInFlight = computed(() =>
  contextItems.value.some(item => item.status === 'uploading')
);

function contextTagType(status: ContextItem['status']): 'default' | 'error' {
  return status === 'failed' ? 'error' : 'default';
}

/**
 * Point the composer at items that already live in the vault or contract
 * management — the "Ask AI" hand-off. The uids are usable immediately; the
 * names are cosmetic and fill in as the lookups land.
 */
function seedContext(selection: {
  fileUids?: string[];
  contractUids?: string[];
  contractFileUids?: string[];
}): void {
  const incoming: { type: ChatContextType; uid: string }[] = [
    ...(selection.fileUids ?? []).map(uid => ({ type: 'file' as const, uid })),
    ...(selection.contractUids ?? []).map(uid => ({ type: 'contract' as const, uid })),
    ...(selection.contractFileUids ?? []).map(uid => ({ type: 'contractFile' as const, uid })),
  ].slice(0, MAX_ASK_AI_ITEMS);

  contextItems.value = incoming.map(({ type, uid }) => ({
    key: contextKey(type, uid),
    type,
    uid,
    name: fallbackContextName(type, uid),
    status: 'ready',
    progress: 100,
  }));

  for (const { type, uid } of incoming) {
    const key = contextKey(type, uid);
    void fetchContextName(type, uid).then(name => {
      if (!name) return;
      const item = contextItems.value.find(entry => entry.key === key);
      if (item) item.name = name;
    });
  }
}

// The chat resolves every reference on every turn, so the same cap the rest of
// the app applies to "Ask AI" applies here.
async function attachFiles(files: File[]): Promise<void> {
  if (files.length === 0) return;

  const room = MAX_ASK_AI_ITEMS - contextItems.value.length;
  if (room <= 0) {
    messageApi.warning(`A question can reference at most ${MAX_ASK_AI_ITEMS} files.`);
    return;
  }

  // Archives are the one type rejected up front: the pipeline would only park
  // them as unreadable after a full upload, and the remedy is different —
  // unpack first. Everything else uploads and lets the pipeline decide.
  const readable = files.filter(file => !isArchive(file.type, file.name));
  if (readable.length < files.length) {
    messageApi.warning(
      "Archives can't be attached — the AI can't read them. Unpack the archive in the vault and attach the files instead."
    );
    if (readable.length === 0) return;
  }

  const accepted = readable.slice(0, room);
  if (accepted.length < readable.length) {
    messageApi.warning(
      `Only ${accepted.length} of ${readable.length} files were attached — the limit is ${MAX_ASK_AI_ITEMS}.`
    );
  }

  // Each add (a pick, a drop) asks once where its files go. A review
  // project's chat stays inside its data room, so a dropped file joins the
  // set under review. Cancelling attaches nothing.
  const uploaded = await openUploadDialog({
    files: accepted,
    scopeRootUid: props.uploadDirectoryUid,
    scopeRootLabel: props.uploadDirectoryUid ? props.uploadDirectoryLabel : undefined,
    max: room,
  });
  if (!uploaded || isUnmounted) return;

  for (const { fileUid, revision } of uploaded) {
    const key = `upload-${createId()}`;
    contextItems.value.push({
      key,
      type: 'file',
      uid: fileUid,
      name: revision.file_name,
      status: 'ready',
      progress: 100,
    });
    // A file uploaded into a data room becomes one of its revisions, so it
    // joins the selection the host holds — the card stays as the visible
    // trace of it, and the host's list is what the next message carries.
    if (isReviewProject.value) {
      uploadedRevisionUidByKey.value[key] = revision.uid;
      emit('add-selected-revisions', [revision.uid]);
    }
  }
}

function removeContextItem(key: string): void {
  const revisionUid = uploadedRevisionUidByKey.value[key];
  if (isReviewProject.value && revisionUid) {
    emit('remove-selected-revisions', [revisionUid]);
    delete uploadedRevisionUidByKey.value[key];
  }
  contextItems.value = contextItems.value.filter(item => item.key !== key);
}

// Clicking the trigger and dropping on the card are two halves of one control.
// naive reports a multi-file pick one file per `change`, all in the same task,
// so they are gathered and attached together: one pick, one upload dialog.
let pickedBatch: File[] = [];

function onFilesPicked(options: { file: UploadFileInfo }): void {
  const file = options.file.file;
  if (!file) return;
  if (pickedBatch.length === 0) {
    setTimeout(() => {
      const batch = pickedBatch;
      pickedBatch = [];
      void attachFiles(batch);
    }, 0);
  }
  pickedBatch.push(file);
}

// Drop lands on the card, as it does in the chat composer — not page-wide, so a
// drag aimed at something else on the page is left alone.
const isDraggingFiles = ref(false);

function canTakeFiles(event: DragEvent): boolean {
  return (
    props.attachable &&
    !props.disabled &&
    !pinnedModelRetired.value &&
    Boolean(event.dataTransfer?.types.includes('Files'))
  );
}

function onDragOver(event: DragEvent): void {
  if (!canTakeFiles(event)) return;
  event.preventDefault();
  isDraggingFiles.value = true;
}

// `dragleave` fires on whichever child the pointer exits, so "left the card"
// has to be read off relatedTarget — comparing target to currentTarget misses
// exits that happen over a child and leaves the overlay stuck.
function onDragLeave(event: DragEvent): void {
  const card = event.currentTarget as HTMLElement | null;
  const entered = event.relatedTarget as Node | null;
  if (!card || !entered || !card.contains(entered)) isDraggingFiles.value = false;
}

function onDrop(event: DragEvent): void {
  if (!canTakeFiles(event)) return;
  event.preventDefault();
  isDraggingFiles.value = false;
  void attachFiles([...(event.dataTransfer?.files ?? [])]);
}

// ── Sending ─────────────────────────────────────────────────────────────────

/**
 * The mode rides along with every frame: it is a per-send preference. The model
 * does not — a thread is pinned to its model when it is created, so only a new
 * thread sends one (see `newThreadModelFields`); a continue or a resume sends
 * none and the backend uses the thread's.
 */
function modeFields(): Pick<GeneralChatStreamRequest, 'confirmation_mode'> {
  return { confirmation_mode: currentConfirmationMode.value };
}

/** Null while no model is available to start a thread with. */
function newThreadModelFields(): Pick<GeneralChatStreamRequest, 'model' | 'llm_provider'> | null {
  const selection = currentChatModelSelection.value;
  if (!selection) return null;
  return { model: selection.model, llm_provider: selection.provider };
}

const canSubmit = computed(
  () =>
    !props.disabled &&
    !pinnedModelRetired.value &&
    !hasUploadsInFlight.value &&
    props.value.trim().length > 0
);

/** How a run ended. The caller needs it: a cancel is not a failure. */
type RunOutcome = 'done' | 'cancelled' | 'failed';

async function runStream(
  start: () => Promise<void>,
  assistantIndex: number,
  failureCopy: string
): Promise<RunOutcome> {
  currentAssistantIndex = assistantIndex;
  statusGroups.clear();
  isStreaming.value = true;
  errorMessage.value = null;
  if (threadUid.value) userAttentionStore.setActiveThread(threadUid.value);

  let outcome: RunOutcome = 'done';
  try {
    await start();
  } catch (error) {
    if (error instanceof ChatStreamCancelledError) {
      // Stopped or superseded by the user — keep whatever tokens already arrived.
      outcome = 'cancelled';
    } else {
      outcome = 'failed';
      logger.error('Gaia chat run failed:', error);
      // A `chat_error` frame carries the backend's user-facing text (e.g. the
      // provider stayed rate-limited through its retries); a dropped socket or a
      // failed send gets the caller's copy instead of its technical message.
      const copy = error instanceof ChatStreamServerError ? error.message : failureCopy;
      errorMessage.value = copy;
      const message = messages.value.at(assistantIndex);
      if (message && !message.content) {
        message.content = copy;
        message.failed = true;
      }
      messageApi.error(copy);
    }
  } finally {
    if (currentAssistantIndex === assistantIndex) currentAssistantIndex = null;
    isStreaming.value = false;
    statusGroups.clear();
  }
  return outcome;
}

/** Appends the empty bubble the run streams into, and returns its index. */
function pushAssistantBubble(): number {
  messages.value.push({ id: createId(), role: 'assistant', content: '' });
  return messages.value.length - 1;
}

/** Whether the run left a card in this bubble the user still has to answer. */
function leftPendingInterrupt(assistantIndex: number): boolean {
  const interrupts = messages.value.at(assistantIndex)?.interrupt?.interrupts;
  return interrupts?.some(item => item.status === 'pending') ?? false;
}

async function send(): Promise<void> {
  if (!canSubmit.value || isStreaming.value) return;

  // Only a new thread needs a model; an existing one is already pinned to its own.
  const modelFields = threadUid.value ? {} : newThreadModelFields();
  if (!modelFields) {
    messageApi.error('No chat model is available yet — try again in a moment.');
    return;
  }

  const question = props.value.trim();
  const selection = readySelection.value;
  const referenced = contextItems.value.filter(item => item.status === 'ready' && item.uid);

  emit('update:value', '');
  emit('update:chatting', true);
  errorMessage.value = null;

  const userMessage: GaiaChatMessageItem = {
    id: createId(),
    role: 'user',
    content: question,
  };

  // The chips are consumed by the send: the message now carries the references
  // and the composer clears for the next question. Follow-ups don't need the
  // uids re-sent — they ride the thread history, and the assistant's tools
  // permission-check per call.
  if (referenced.length > 0) {
    userMessage.selectedDocumentsIncluded = true;
    userMessage.selectedDocumentCount = referenced.length;
    userMessage.selectedDocumentNames = referenced.map(item => item.name);
    userMessage.selectedDocumentKeys = referenced.map(item => item.key);
  }
  contextItems.value = [];

  if (!threadUid.value) threadTitle.value = buildTitle(question);

  messages.value.push(userMessage);
  const assistantIndex = pushAssistantBubble();
  scrollToBottom();

  const outcome = await runStream(
    () =>
      chatStream.startChat({
        message: question,
        thread_uid: threadUid.value,
        title: threadUid.value ? null : buildTitle(question),
        ...modeFields(),
        ...modelFields,
        /*
         * The two kinds scope a message differently. A review project's chat is
         * bound to one data room: the project uid on a new thread (an existing
         * one already carries it) and the revisions currently picked there. The
         * general chat's scope is whatever the composer collected — vault
         * files, contracts, contract files — which is what its set-based
         * document tools operate over.
         */
        ...(isReviewProject.value
          ? {
              review_project_uid: threadUid.value ? null : (props.reviewProjectUid ?? null),
              selected_revision_uids: selectedRevisionUids.value,
            }
          : {
              ...(selection.file.length > 0 ? { selected_vault_file_uids: selection.file } : {}),
              ...(selection.contract.length > 0
                ? { selected_contract_uids: selection.contract }
                : {}),
              ...(selection.contractFile.length > 0
                ? { selected_contract_file_uids: selection.contractFile }
                : {}),
            }),
      }),
    assistantIndex,
    'Sorry, I could not complete that request. Please try again.'
  );

  const settledUid = threadUid.value;
  if (settledUid && outcome === 'done' && !leftPendingInterrupt(assistantIndex)) {
    void noteRunSettled(settledUid);
  }

  // The thread this run created (or renamed) has to show up in History.
  void refreshThreads();
}

/** Answering an interrupt resumes the same thread — a new run on the same turn. */
async function submitHitlDecision(
  interrupt: NormalizedInterrupt,
  payload: HitlSubmitPayload
): Promise<void> {
  const currentThreadUid = threadUid.value;
  if (isStreaming.value || interrupt.status !== 'pending' || !currentThreadUid) return;

  interrupt.status = 'submitting';
  interrupt.errorMessage = null;

  messages.value.push({
    id: createId(),
    role: 'user',
    content: summarizeHitlSubmission(interrupt, payload),
  });
  const assistantIndex = pushAssistantBubble();
  scrollToBottom();

  // Fired before the resume stream settles, so a host can reconcile at once —
  // the review workspace soft-locks its report editor the moment a report
  // generation is confirmed, rather than when the run finishes.
  if (payload.shape === 'langgraph_action') {
    emit('interrupt-decision', {
      interruptType: interrupt.businessType,
      action: payload.decisions[0]?.type ?? '',
    });
  }

  const outcome = await runStream(
    () =>
      chatStream.resumeChat({
        thread_uid: currentThreadUid,
        interrupt_id: interrupt.interruptId,
        ...(payload.shape === 'tool_confirmation' || payload.shape === 'user_question'
          ? { resume_value: payload.resumeValue }
          : { decisions: payload.decisions }),
        ...modeFields(),
      }),
    assistantIndex,
    'Sorry, I could not apply that decision. Please try again.'
  );

  // Anything but a clean finish leaves the card answerable again — a cancelled
  // resume is not a decision the backend acted on.
  interrupt.status = outcome === 'done' ? 'resolved' : 'pending';
  if (outcome === 'failed') interrupt.errorMessage = errorMessage.value;

  if (outcome === 'done' && !leftPendingInterrupt(assistantIndex)) {
    void noteRunSettled(currentThreadUid);
  }

  void refreshThreads();
}

function stop(): void {
  chatStream.cancelChat();
}

// Picking a suggestion loads it into the composer rather than asking it
// outright, so the question stays visible and editable before it is sent.
function pickSuggestion(question: string): void {
  if (!props.disabled) emit('update:value', question);
}

// ── Threads ─────────────────────────────────────────────────────────────────

const threads = ref<ChatThreadInDb[]>([]);
const threadsLoading = ref(false);
const historyOpen = ref(false);
/** History shows archived chats too — drives `include_archived` on the search. */
const archivedVisible = ref(false);

/**
 * The loaded thread as the backend returned it. Kept whole (rather than just
 * uid + title) because `thread_metadata` carries `referenced_items`, which the
 * "used in this thread" popover reads.
 */
const currentThread = ref<ChatThreadWithMessages | null>(null);

/**
 * The model the open thread is pinned to, for the settings pill and panel. A
 * brand-new thread is pinned the moment the backend names it, before its record
 * is fetched, so until then it is the model the run was started with.
 */
const pinnedModel = computed(() => {
  const thread = currentThread.value;
  if (thread) return { provider: thread.llm_provider, model: thread.llm_model };
  return threadUid.value ? currentChatModelSelection.value : null;
});

/** The open thread's model was retired and a thread can't change model: read-only. */
const pinnedModelRetired = computed(() => {
  const thread = currentThread.value;
  return thread != null && isRetiredChatModel(thread.llm_provider, thread.llm_model);
});

/** An untouched new chat — nothing to start over from, so "New chat" is a no-op. */
const isEmptyNewChat = computed(
  () => props.chatting && !threadUid.value && messages.value.length === 0
);

const threadMeta = computed(() => {
  if (!threadUid.value) return 'Just now · this conversation';
  const count = messages.value.filter(message => message.role === 'user').length;
  return `${count} message${count === 1 ? '' : 's'} · this conversation`;
});

async function refreshThreads(): Promise<void> {
  threadsLoading.value = true;
  try {
    const response = await threadApi.value.search({
      body: {},
      ...(archivedVisible.value ? { query: { include_archived: true } } : {}),
    });
    if (response.data) threads.value = response.data.results;
  } catch (error) {
    logger.error('Failed to load chat threads:', error);
  } finally {
    threadsLoading.value = false;
  }
}

function openHistory(): void {
  historyOpen.value = true;
  void refreshThreads();
}

function setArchivedVisible(visible: boolean): void {
  archivedVisible.value = visible;
  void refreshThreads();
}

// ── Thread admin ────────────────────────────────────────────────────────────
// The writes live here because the list does: GaiaChatHistory owns which row is
// being edited, this component owns what the rows are.

/** Patch one thread in place, so a rename shows before the refetch lands. */
function patchThread(updated: ChatThreadInDb): void {
  threads.value = threads.value.map(thread => (thread.uid === updated.uid ? updated : thread));
  if (threadUid.value === updated.uid) {
    // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
    threadTitle.value = updated.title?.trim() || 'Untitled chat';
  }
}

async function renameThread(uid: string, title: string): Promise<void> {
  const trimmed = title.trim();
  if (!trimmed) return;
  try {
    const response = await threadApi.value.update({
      path: { thread_uid: uid },
      body: { title: trimmed },
    });
    if (response.data) patchThread(response.data);
  } catch (error) {
    logger.error('Failed to rename thread:', error);
    messageApi.error('Could not rename that chat.');
    // Refetch so the row snaps back to what the server actually holds.
    void refreshThreads();
  }
}

async function setThreadArchived(uid: string, archived: boolean): Promise<void> {
  try {
    const response = await threadApi.value.update({
      path: { thread_uid: uid },
      body: { is_archived: archived },
    });
    if (response.data) patchThread(response.data);
    // Archiving drops the row out of the default list, so the list has to be
    // re-read rather than patched.
    if (!archivedVisible.value) void refreshThreads();
  } catch (error) {
    logger.error('Failed to archive thread:', error);
    messageApi.error(`Could not ${archived ? 'archive' : 'restore'} that chat.`);
    void refreshThreads();
  }
}

async function removeThread(uid: string): Promise<void> {
  try {
    await threadApi.value.remove({ path: { thread_uid: uid } });
    threads.value = threads.value.filter(thread => thread.uid !== uid);
    // Nothing left to draw attention to.
    userAttentionStore.clearThread(uid);

    if (threadUid.value === uid) {
      // The thread is gone, so its run has nothing to write to — and the host
      // must drop `?thread_uid=` or back/forward would reopen a 404.
      chatStream.detach();
      resetTranscript();
      emit('thread-change', null);
    }
  } catch (error) {
    logger.error('Failed to delete thread:', error);
    messageApi.error('Could not delete that chat.');
    void refreshThreads();
  }
}

// ── Used in this thread ─────────────────────────────────────────────────────

const referencedItems = computed(() =>
  currentThread.value ? parseReferencedItems(currentThread.value.thread_metadata) : []
);

/** Resolved names, keyed by context key. Survives reopening the popover. */
const referencedNames = ref<Record<string, string>>({});

function referencedLabel(item: ReferencedItem): string {
  return referencedNames.value[item.key] ?? fallbackContextKind(item.type);
}

/**
 * Resolution starts when the popover opens, not when the thread loads — each
 * uid is one metadata request and most visits never open it.
 */
function onReferencesShow(show: boolean): void {
  if (!show) return;
  for (const item of referencedItems.value) {
    if (item.key in referencedNames.value) continue;
    void fetchContextName(item.type, item.uid).then(name => {
      if (name) referencedNames.value = { ...referencedNames.value, [item.key]: name };
    });
  }
}

/** Which glyph stands for a referenced item, in the menu and in the popover. */
function referencedIcon(item: ReferencedItem): Component {
  return item.type === 'contract' ? IconDocumentLock : IconDocumentText;
}

/**
 * The thread header's three controls, stated once and rendered two ways: as
 * buttons beside a references popover where there is room, and folded into one
 * ⋯ menu where there is not (a 360px rail fits a title or three buttons, not
 * both). Labels, glyphs and disabled rules live here rather than in each
 * branch, so the narrow chat can never end up offering something the wide one
 * doesn't — or calling it something else.
 */
const threadActions = computed(() => ({
  newChat: {
    label: 'New chat',
    menuLabel: 'New chat',
    icon: IconAdd,
    disabled: isEmptyNewChat.value,
    hint: isEmptyNewChat.value ? 'This is already a new chat' : 'Start a new chat',
  },
  history: {
    // A button is read beside its glyph and has a header to fit into; a menu
    // row stands alone and can afford to say what it opens.
    label: 'History',
    menuLabel: 'Earlier chats',
    icon: IconTime,
    disabled: false,
    hint: 'Earlier chats',
  },
  // Only rendered once the thread has touched something — until then there is
  // nothing to open, and an empty control is noise in a header this tight.
  references: {
    label: 'Used in this thread',
    // Names its own count: as a menu row it has no badge to carry it.
    menuLabel: `Used in this thread (${referencedItems.value.length})`,
    icon: IconAttach,
    hint: `${referencedItems.value.length} document${
      referencedItems.value.length === 1 ? '' : 's'
    } used in this thread`,
  },
}));

/** The same three, as a dropdown; references keeps its list as a submenu. */
const threadMenuOptions = computed<DropdownOption[]>(() => {
  const actions = threadActions.value;
  const menuIcon =
    (component: Component): (() => VNode) =>
    () =>
      h(NIcon, { component });
  return [
    {
      key: 'new-chat',
      label: actions.newChat.menuLabel,
      disabled: actions.newChat.disabled,
      icon: menuIcon(actions.newChat.icon),
    },
    {
      key: 'history',
      label: actions.history.menuLabel,
      icon: menuIcon(actions.history.icon),
    },
    // Same rule as the wide header's popover: no references, no row.
    ...(referencedItems.value.length
      ? [
          { key: 'references-divider', type: 'divider' } satisfies DropdownOption,
          {
            key: 'references',
            label: actions.references.menuLabel,
            icon: menuIcon(actions.references.icon),
            children: referencedItems.value.map(item => ({
              key: `reference:${item.key}`,
              label: referencedLabel(item),
              icon: menuIcon(referencedIcon(item)),
            })),
          } satisfies DropdownOption,
        ]
      : []),
  ];
});

function onThreadMenuSelect(key: string): void {
  if (key === 'new-chat') {
    newChat();
    return;
  }
  if (key === 'history') {
    openHistory();
    return;
  }
  const referenceKey = key.startsWith('reference:') ? key.slice('reference:'.length) : null;
  const item = referenceKey
    ? referencedItems.value.find(entry => entry.key === referenceKey)
    : undefined;
  if (item) emit('open-reference', contextItemRoute(item.type, item.uid));
}

/** Enter chat mode on an empty thread — the design's "Open chat" affordance. */
function expandChat(): void {
  chatStream.detach();
  resetTranscript();
  emit('update:chatting', true);
}

function resetTranscript(): void {
  // No thread is on screen any more, so attention events for the one we were
  // reading should badge the sidebar again.
  userAttentionStore.setActiveThread(null);
  messages.value = [];
  threadUid.value = null;
  currentThread.value = null;
  threadTitle.value = 'New conversation';
  errorMessage.value = null;
  contextItems.value = [];
  uploadedRevisionUidByKey.value = {};
  // A new conversation is scoped to nothing yet, so the context the last one
  // carried is released — for a review project that means the data-room
  // selection the host was holding on this chat's behalf.
  if (isReviewProject.value) emit('clear-selected-revisions');
  statusGroups.clear();
  isStreaming.value = false;
  currentAssistantIndex = null;
}

function newChat(): void {
  if (isEmptyNewChat.value) return;
  // Switching away leaves any in-flight run to the worker rather than killing
  // it — it flags its own thread for attention when it settles.
  chatStream.detach();
  resetTranscript();
  emit('update:value', '');
  emit('update:chatting', true);
  emit('thread-change', null);
}

function applyTranscript(thread: ChatThreadWithMessages): void {
  threadUid.value = thread.uid;
  currentThread.value = thread;
  // eslint-disable-next-line @typescript-eslint/prefer-nullish-coalescing -- empty string must use the fallback
  threadTitle.value = thread.title?.trim() || 'Untitled chat';
  messages.value = thread.messages
    .filter(message => typeof message.content === 'string' && message.content.trim().length > 0)
    .map((message, index) => {
      const role = (message.role || '').toLowerCase() === 'user' ? 'user' : 'assistant';
      return {
        id: `thread-msg-${index}-${role}`,
        role,
        // Both grammars prepend `<selected_documents>`, so a restored thread has
        // to strip it back out of the user's bubble.
        ...(role === 'user'
          ? parseSelectedDocumentsMessage(message.content)
          : { content: message.content }),
      } satisfies GaiaChatMessageItem;
    });

  const interrupts = normalizeInterruptPayload(extractPendingInterrupt(thread));
  const lastAssistant = [...messages.value].reverse().find(message => message.role === 'assistant');
  if (interrupts.length && lastAssistant) lastAssistant.interrupt = { interrupts };
}

/**
 * Rejoin a run still executing on the worker. Frames replay from the run's
 * first one, so the bubble it attaches to must be freshly empty or the answer
 * duplicates.
 */
async function attachToActiveRun(thread: ChatThreadWithMessages): Promise<void> {
  const activeRun = thread.active_run;
  if (!activeRun) return;
  if (activeRun.status !== TaskStatus.PENDING && activeRun.status !== TaskStatus.RUNNING) return;

  const attachedUid = thread.uid;
  const assistantIndex = pushAssistantBubble();
  scrollToBottom();

  await runStream(
    () => chatStream.attachRun(activeRun.run_id),
    assistantIndex,
    'Lost the connection to the running chat.'
  );

  // The run's own frames are not authoritative after a rejoin — the thread GET is.
  if (!isUnmounted && threadUid.value === attachedUid) await refreshThread(attachedUid);
}

async function refreshThread(uid: string): Promise<void> {
  try {
    const response = await threadApi.value.getById({ path: { thread_uid: uid } });
    if (!response.data || threadUid.value !== uid) return;
    applyTranscript(response.data);
    userAttentionStore.setActiveThread(uid);
    scrollToBottom();
  } catch (error) {
    logger.error('Failed to refresh thread:', error);
  }
}

/**
 * A run the user watched settle needs no badge anywhere. The live
 * `user_attention` event normally consumes the flag (see
 * `userAttentionStore`), but the socket is best-effort, so refetching the thread
 * clears it server-side too — and picks up the `referenced_items` the run added.
 *
 * Only the thread itself is refreshed, never the messages: we saw every token,
 * so the transcript needs no repair. Callers scope this to the case the store
 * acknowledges — a clean finish with nothing left to answer. A failed run or a
 * pending interrupt keeps its badge, so it is still there if the user walks
 * away without acting on it.
 */
async function noteRunSettled(uid: string): Promise<void> {
  if (isUnmounted) return;
  userAttentionStore.clearThread(uid);
  try {
    const response = await threadApi.value.getById({ path: { thread_uid: uid } });
    if (response.data && threadUid.value === uid) currentThread.value = response.data;
  } catch (error) {
    logger.error('Failed to refresh the settled thread:', error);
  }
}

async function openThread(uid: string): Promise<void> {
  if (uid === threadUid.value) return;
  chatStream.detach();
  resetTranscript();
  emit('update:chatting', true);
  try {
    const response = await threadApi.value.getById({ path: { thread_uid: uid } });
    if (!response.data) throw new Error('Failed to load thread');
    applyTranscript(response.data);
    emit('thread-change', uid);
    // Opening a thread is what clears its flag server-side; mirror that locally.
    userAttentionStore.setActiveThread(uid);
    scrollToBottom();
    await attachToActiveRun(response.data);
  } catch (error) {
    logger.error('Failed to open thread:', error);
    messageApi.error('Failed to open that chat.');
  }
}

// The host owns the URL (this component is also mounted where there is no
// router), so it drives these when the user navigates back or forward — and
// `openHistory` lets it hand the user straight to the thread list.
defineExpose({ openThread, newChat, openHistory, seedContext });

/**
 * The socket came back. A run survives it dying, so the worker may still be
 * streaming a thread we lost mid-answer — refetch and rejoin.
 */
async function rejoinAfterReconnect(uid: string): Promise<void> {
  try {
    const response = await threadApi.value.getById({ path: { thread_uid: uid } });
    if (!response.data?.active_run) return;
    // Re-check: the await gave the user time to switch threads or unmount.
    if (threadUid.value !== uid || isStreaming.value || isUnmounted) return;
    applyTranscript(response.data);
    await attachToActiveRun(response.data);
  } catch (error) {
    logger.error('Failed to rejoin the running chat after reconnect:', error);
  }
}

const disposeSocketListener = getWsClient().addStatusListener(status => {
  if (status !== 'open') return;
  const uid = threadUid.value;
  if (!uid || isStreaming.value || isUnmounted) return;
  void rejoinAfterReconnect(uid);
});

onBeforeUnmount(() => {
  isUnmounted = true;
  userAttentionStore.setActiveThread(null);
  disposeSocketListener();
  // Detach, never cancel: the run keeps going on the worker and flags its
  // thread for attention when it settles.
  chatStream.detach();
});

// Leaving chat mode from the outside (a nav "Home" click) must not leave a run
// writing into a transcript nobody can see.
watch(
  () => props.chatting,
  chatting => {
    if (!chatting) {
      chatStream.detach();
      resetTranscript();
    }
  }
);

// ── Inline title rename ─────────────────────────────────────────────────────

/** Non-null while the header title is an input. */
/** Non-null while the title is being edited; the field owns the draft. */
const renamingTitle = ref(false);

function startRenameTitle(): void {
  if (!threadUid.value) return;
  renamingTitle.value = true;
}

// The field only commits a trimmed, changed, non-empty value, so there is
// nothing left to guard here.
function commitRenameTitle(title: string): void {
  renamingTitle.value = false;
  const uid = threadUid.value;
  if (!uid) return;
  threadTitle.value = title;
  void renameThread(uid, title);
}

function cancelRenameTitle(): void {
  renamingTitle.value = false;
}

// ── Presentation ────────────────────────────────────────────────────────────

// The composer card owns all the chrome; the textarea inside is bare.
const textareaOverrides = {
  border: 'none',
  borderHover: 'none',
  borderFocus: 'none',
  borderDisabled: 'none',
  boxShadowFocus: 'none',
  color: 'transparent',
  colorFocus: 'transparent',
  colorDisabled: 'transparent',
  paddingMedium: '0',
  lineHeightTextarea: '1.5',
};

// The arrow dims until there is a question to send (mock 5a) — naive's own
// disabled treatment fades the whole button, so the two states are colors.
const sendOverrides = {
  colorDisabled: gaiaNeutral.inkDisabled,
  borderDisabled: 'none',
  textColorDisabled: gaiaNeutral.surface,
  opacityDisabled: '1',
};
</script>

<template>
  <!-- Card, transcript and suggestion row are one unit. The card element is the
       same node in both states — putting it behind a v-if branch per state
       would remount it and kill the morph. -->
  <n-flex
    vertical
    :size="0"
    class="gaia-chat"
    :class="{ 'gaia-chat--chatting': props.chatting }"
    @dragover="onDragOver"
    @dragleave="onDragLeave"
    @drop="onDrop"
  >
    <!-- The whole surface takes the drop, not just the card: in chat mode the
         card is a strip at the bottom and the transcript is most of what the
         user is looking at, so aiming at the card would be a bullseye test.
         pointer-events:none so the drag keeps reaching what's underneath. -->
    <n-flex
      v-if="isDraggingFiles"
      align="center"
      justify="center"
      :size="0"
      :wrap="false"
      class="drop-overlay"
    >
      <gaia-text weight="semibold" tone="ink">{{ props.dropHint }}</gaia-text>
    </n-flex>

    <n-flex
      v-if="props.chatting"
      class="thread-header"
      align="center"
      :size="12"
      :wrap="false"
      justify="space-between"
    >
      <div :style="{ minWidth: 0 }">
        <!-- Click-to-rename: the title is the control, so there is no pencil to
             hunt for. Only a saved thread can be renamed — a new one has no uid
             yet and takes its name from the first question. -->
        <gaia-inline-edit
          v-if="renamingTitle"
          :value="threadTitle"
          size="small"
          @commit="commitRenameTitle"
          @cancel="cancelRenameTitle"
        />
        <!-- A step down in a narrow host: at 16px the conversation's name
             outranks the pane title above it, which is the heading that says
             where you are. -->
        <gaia-text
          v-else
          truncate
          :size="props.dense ? 'body' : 'subtitle'"
          weight="semibold"
          tone="ink"
          :class="{ 'thread-title--editable': Boolean(threadUid) }"
          :title="threadUid ? 'Click to rename' : undefined"
          :tabindex="threadUid ? 0 : undefined"
          :style="{ display: 'block' }"
          @click="startRenameTitle"
          @keydown.enter.prevent="startRenameTitle"
        >
          {{ threadTitle }}
        </gaia-text>
        <gaia-text truncate size="meta" tone="faint">{{ threadMeta }}</gaia-text>
      </div>
      <n-flex align="center" :size="8" :wrap="false" :style="{ flex: 'none' }">
        <!-- Narrow host: the same three controls behind one ⋯, because a title
             plus three buttons does not fit a rail. -->
        <gaia-dropdown
          v-if="props.dense"
          :options="threadMenuOptions"
          placement="bottom-end"
          @select="onThreadMenuSelect"
        >
          <gaia-icon-button :dimension="28" title="Chat actions" aria-label="Chat actions">
            <n-icon :component="IconMore" :size="17" />
          </gaia-icon-button>
        </gaia-dropdown>

        <!-- Everything this conversation has touched. Absent until there is
             something to open — an empty popover is a dead end. -->
        <gaia-popover
          v-if="!props.dense && referencedItems.length"
          placement="bottom-end"
          @update:show="onReferencesShow"
        >
          <template #trigger>
            <gaia-icon-button :aria-label="threadActions.references.hint">
              <n-badge :value="referencedItems.length" :max="9">
                <n-icon :component="threadActions.references.icon" :size="17" />
              </n-badge>
            </gaia-icon-button>
          </template>
          <gaia-menu-surface width="320px">
            <gaia-text
              variant="label"
              size="micro"
              tone="faint"
              tag="div"
              :style="{ padding: '2px 10px 8px' }"
            >
              Used in this thread
            </gaia-text>
            <n-scrollbar style="max-height: 260px">
              <!-- These rows navigate, so they take GaiaLink's recipe — accent,
                   semibold, `dense` — rather than the neutral one GaiaColumnsMenu
                   and GaiaCheckMenu give their option rows. The size sits on the
                   row, not the text: the glyph centres against the content's
                   line box, which is the shell's 20px until the row names one. -->
              <gaia-list-item
                v-for="item in referencedItems"
                :key="item.key"
                size="dense"
                @click="emit('open-reference', contextItemRoute(item.type, item.uid))"
              >
                <template #leading>
                  <n-icon
                    :component="referencedIcon(item)"
                    :size="15"
                    :color="gaiaNeutral.textMuted"
                  />
                </template>
                <gaia-text tone="accent" weight="semibold">
                  {{ referencedLabel(item) }}
                </gaia-text>
                <template #trailing>
                  <gaia-text size="meta" tone="faint">
                    {{ fallbackContextKind(item.type) }}
                  </gaia-text>
                </template>
              </gaia-list-item>
            </n-scrollbar>
          </gaia-menu-surface>
        </gaia-popover>
        <gaia-button
          v-if="!props.dense"
          size="small"
          data-tour="chat.history"
          :title="threadActions.history.hint"
          :spam-proof="false"
          @click="openHistory"
        >
          <template #icon>
            <n-icon :component="threadActions.history.icon" />
          </template>
          {{ threadActions.history.label }}
        </gaia-button>
        <!-- Slotted label: "New chat" is the idiom for a thread, and it is the
             same string the overflow menu shows. -->
        <gaia-create-button
          v-if="!props.dense"
          size="sm"
          data-tour="chat.new-chat"
          :disabled="threadActions.newChat.disabled"
          :spam-proof="false"
          :title="threadActions.newChat.hint"
          @click="newChat"
        >
          {{ threadActions.newChat.label }}
        </gaia-create-button>
      </n-flex>
    </n-flex>

    <n-scrollbar
      v-if="props.chatting"
      ref="threadScroll"
      :style="{ flex: '1 1 auto', minHeight: 0 }"
    >
      <gaia-chat-thread
        :messages="messages"
        :streaming="isStreaming"
        :status-groups="visibleStatusGroups"
        :user-initials="props.userInitials"
        :busy="isStreaming"
        @hitl-submit="submitHitlDecision"
        @open-reference="path => emit('open-reference', path)"
      />
    </n-scrollbar>

    <gaia-banner v-if="pinnedModelRetired" type="warning">
      This chat used a model that's no longer available. Start a new chat to continue.
      <template #action>
        <gaia-button size="small" @click="newChat">New chat</gaia-button>
      </template>
    </gaia-banner>

    <div class="card" data-tour="chat.composer" :class="{ 'card--compact': props.chatting }">
      <gaia-button
        v-if="!props.chatting"
        class="expand"
        quaternary
        size="small"
        data-tour="chat.open-chat"
        title="Open chat"
        :spam-proof="false"
        @click="expandChat"
      >
        <template #icon>
          <n-icon :component="IconExpand" />
        </template>
        Open chat
      </gaia-button>

      <!-- Attached-file chips, above the question — same place the chat
           composer puts them. A review project's data-room selection joins them
           here: it is context for the next message exactly as an attachment is,
           so it belongs in the same row rather than in a second one. -->
      <div
        v-if="contextItems.length || selectedRevisionChips.length"
        :style="{ marginBottom: '10px' }"
      >
        <n-flex :size="8">
          <gaia-tag
            v-for="revision in selectedRevisionChips"
            :key="`revision-${revision.uid}`"
            closable
            @close="emit('remove-selected-revisions', [revision.uid])"
          >
            {{ revision.name ?? 'Data-room file' }}
          </gaia-tag>
          <gaia-tag
            v-for="item in contextItems"
            :key="item.key"
            closable
            :type="contextTagType(item.status)"
            @close="removeContextItem(item.key)"
          >
            <template v-if="item.status === 'uploading'" #icon>
              <n-spin :size="12" />
            </template>
            {{ item.name }}
            <template v-if="item.status === 'uploading'"> · {{ item.progress }}%</template>
          </gaia-tag>
        </n-flex>
      </div>

      <gaia-prompt-textarea
        :value="props.value"
        :placeholder="props.chatting ? props.followUpPlaceholder : props.placeholder"
        :disabled="props.disabled || pinnedModelRetired"
        :autosize="{ minRows: props.chatting ? 1 : 2 }"
        :theme-overrides="textareaOverrides"
        @update:value="(v: string) => emit('update:value', v)"
        @submit="send"
      />

      <!-- Footer: attach + chat settings on the left, the send arrow pinned
           right. The cluster takes the slack so its contents stay left-bound. -->
      <n-flex
        align="center"
        :size="12"
        :wrap="false"
        :style="{
          borderTop: `1px solid ${gaiaNeutral.borderSubtle}`,
          paddingTop: '12px',
          marginTop: '8px',
        }"
      >
        <n-flex align="center" :size="8" :wrap="false" :style="{ flex: '1 1 auto', minWidth: 0 }">
          <gaia-file-upload
            v-if="props.attachable && !pinnedModelRetired"
            multiple
            data-tour="chat.attach"
            :dragging="isDraggingFiles"
            :default-upload="false"
            :show-file-list="false"
            :file-list="[]"
            @change="onFilesPicked"
          >
            {{ props.dense ? '' : 'Attach or drop files' }}
          </gaia-file-upload>
          <gaia-chat-settings
            :dense="props.dense"
            :placement="props.chatting ? 'top-start' : 'bottom-start'"
            :pinned-model="pinnedModel"
          />
        </n-flex>
        <gaia-icon-button
          v-if="isStreaming"
          class="send"
          circle
          type="primary"
          :dimension="36"
          aria-label="Stop generating"
          title="Stop generating"
          @click="stop"
        >
          <n-icon :component="IconStop" :size="14" />
        </gaia-icon-button>
        <gaia-icon-button
          v-else
          class="send"
          circle
          type="primary"
          :dimension="36"
          :disabled="!canSubmit"
          :theme-overrides="sendOverrides"
          aria-label="Send message"
          @click="send"
        >
          <n-icon :component="IconArrowUp" :size="17" />
        </gaia-icon-button>
      </n-flex>
    </div>

    <n-flex
      v-if="!props.chatting && props.suggestions.length"
      :size="8"
      :style="{ marginTop: '12px' }"
    >
      <gaia-chip
        v-for="question in props.suggestions"
        :key="question"
        variant="secondary"
        :disabled="props.disabled"
        @click="pickSuggestion(question)"
      >
        {{ question }}
      </gaia-chip>
    </n-flex>

    <gaia-chat-history
      v-model:show="historyOpen"
      :threads="threads"
      :loading="threadsLoading"
      :active-uid="threadUid ?? ''"
      :new-chat-disabled="isEmptyNewChat"
      :archived-visible="archivedVisible"
      @update:archived-visible="setArchivedVisible"
      @select="openThread"
      @new-chat="newChat"
      @rename="renameThread"
      @archive="setThreadArchived"
      @delete="removeThread"
    />
  </n-flex>
</template>

<style scoped>
/* The composer card from mock 5a: ink border, halo-ring shadow, footer row
   with a hairline top border. This is a bespoke surface — no naive component
   carries a composed shadow or an internal footer rule, so the chrome is CSS
   on our own element (values come from the shared tokens). */
.gaia-chat {
  position: relative;
  min-height: 0;
  /* Stated, not inherited: hosts centre the chat with a row flex container
     (`n-flex justify="center"`), where a flex item sizes to its content on the
     main axis instead of stretching. Without this the card collapses to the
     composer's max-content width; the host caps the measure with max-width. */
  width: 100%;
}

/* Chat mode: the transcript takes the slack and scrolls, the card pins to the
   bottom. The height has to be stated rather than inherited — the host page is
   a plain column, so there is no `height: 100%` chain to ride. It is the
   viewport less Layout's own vertical padding, read from the token Layout
   reads: every host mounts the chat as that column's only uncollapsed row, so
   this one rule puts /chat and home's chat on the same y. */
.gaia-chat--chatting {
  height: calc(100vh - 2 * v-bind('gaiaShellPadding.top'));
  transition: height v-bind('gaiaMotion.morph');
}

/* The title doubles as the rename control. naive has no "editable text" state,
   so the affordance is ours: a hover wash the width of the text. */
.thread-title--editable {
  cursor: text;
  border-radius: 6px;
  padding: 1px 4px;
  margin-left: -4px;
  transition: background 120ms ease;
}

.thread-title--editable:hover {
  background: v-bind('gaiaNeutral.surfaceHover');
}

.thread-header {
  flex: none;
  padding-bottom: 14px;
  margin-bottom: 6px;
  border-bottom: 1px solid v-bind('gaiaNeutral.borderSubtle');
}

.card {
  position: relative;
  flex: none;
  background: v-bind('gaiaNeutral.surface');
  border: 1px solid v-bind('gaiaNeutral.ink');
  border-radius: 14px;
  box-shadow: v-bind('gaiaShadow.composer');
  padding: 20px 20px 14px;
  transition: padding v-bind('gaiaMotion.morph');
}

.card--compact {
  padding: 14px 20px 12px;
  margin-top: 12px;
}

/* Drag-time state: the card says it will take the files, dashed like the
   attach trigger it stands in for. */
.drop-overlay {
  position: absolute;
  inset: 0;
  z-index: 1;
  border: 1px dashed v-bind('gaiaNeutral.ink');
  border-radius: 14px;
  background: v-bind('gaiaNeutral.surfaceTint');
  pointer-events: none;
}

/* Sits over the textarea's top-right corner, so it can't be a flow sibling.
   The 10px offset is the button's own horizontal padding read backwards, so
   the label — not the hover wash — lands on the card's content edge, where the
   send arrow below it sits. */
.expand {
  position: absolute;
  top: 10px;
  right: 10px;
  z-index: 2;
}

.send {
  flex: none;
}
</style>
